import BaseApiClient from '../BaseApiClient';
import type {
  AggregatedChatResult,
  ApplyFiltersRequest,
  ApplyFiltersResponse,
  AudienceChatRequest,
  AudienceDetailResponse,
  AudienceListResponse,
  DeleteAudienceResponse,
  FilterOptionsRequest,
  FilterOptionsResponse,
  PropertiesResponse,
  RecentPromptsResponse,
  ExportAudienceRequest,
  PreviewRequest,
  PreviewResponse,
  SaveAudienceRequest,
  SaveAudienceResponse,
  ScvStatsResponse,
  CsvUploadResponse
} from '@/interfaces/AudienceManifest.interface';
import { looksLikeSegmentSql, streamChatChunks } from './sse-parser';
import {
  getManifestFetchInit,
  manifestApiPath
} from './manifest-request';

export type ChatStreamCallbacks = {
  onThought?: (text: string) => void;
  onContent?: (delta: string) => void;
};

export class ApiAudienceManifest extends BaseApiClient {
  private manifestUrl(suffix: string): string {
    const base = String(this.client.defaults.baseURL).replace(/\/$/, '');
    return `${base}${manifestApiPath(suffix)}`;
  }

  async recentPrompts(): Promise<RecentPromptsResponse> {
    const response = await this.client.get<RecentPromptsResponse>(
      manifestApiPath('/api/audience/recent-prompts')
    );
    return response.data;
  }

  async chatStream(
    payload: AudienceChatRequest,
    options?: ChatStreamCallbacks
  ): Promise<AggregatedChatResult> {
    const body = {
      ...payload,
      session_id: payload.session_id ?? null,
      tenant_id: payload.tenant_id ?? 'nexus-default'
    };

    const response = await fetch(
      this.manifestUrl('/api/audience/chat'),
      getManifestFetchInit(
        'POST',
        'text/event-stream',
        JSON.stringify(body)
      )
    );

    if (!response.ok) {
      throw new Error('Chat request failed');
    }
    if (!response.body) {
      throw new Error('Chat response body is empty');
    }

    let contentAccumulator = '';

    const aggregated = await streamChatChunks(response.body, (chunk) => {
      if (chunk.type === 'thought' && chunk.text) {
        options?.onThought?.(chunk.text);
      }
      if (chunk.type === 'content' && chunk.text) {
        contentAccumulator += chunk.text;
        if (
          looksLikeSegmentSql(contentAccumulator) ||
          /```sql/i.test(contentAccumulator)
        ) {
          return;
        }
        options?.onContent?.(chunk.text);
      }
    });

    return {
      ...aggregated,
      sessionId: aggregated.sessionId ?? payload.session_id ?? null
    };
  }

  async properties(): Promise<PropertiesResponse> {
    const response = await this.client.get<PropertiesResponse>(
      manifestApiPath('/api/audience/properties')
    );
    return response.data;
  }

  async getScvStats(): Promise<ScvStatsResponse> {
    const response = await this.client.get<ScvStatsResponse>(
      manifestApiPath('/api/audience/scv-stats')
    );
    return response.data;
  }

  async filterOptions(
    payload: FilterOptionsRequest
  ): Promise<FilterOptionsResponse> {
    const response = await this.client.post<FilterOptionsResponse>(
      manifestApiPath('/api/audience/filter-options'),
      {
        ...payload,
        tenant_id: payload.tenant_id ?? 'nexus-default'
      }
    );
    return response.data;
  }

  async previewAudience(payload: PreviewRequest): Promise<PreviewResponse> {
    const response = await this.client.post<PreviewResponse>(
      manifestApiPath('/api/audience/preview'),
      {
        ...payload,
        tenant_id: payload.tenant_id ?? 'nexus-default'
      }
    );
    return response.data;
  }

  async getAudiencePreview(
    audienceId: string,
    params?: {
      page?: number;
      page_size?: number;
      sort_column?: string | null;
      sort_direction?: 'ASC' | 'DESC' | null;
    }
  ): Promise<PreviewResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page != null) searchParams.set('page', String(params.page));
    if (params?.page_size != null) {
      searchParams.set('page_size', String(params.page_size));
    }
    if (params?.sort_column) {
      searchParams.set('sort_column', params.sort_column);
    }
    if (params?.sort_direction) {
      searchParams.set('sort_direction', params.sort_direction);
    }
    const query = searchParams.toString();
    const response = await this.client.get<PreviewResponse>(
      manifestApiPath(
        `/api/audience/${audienceId}/preview${query ? `?${query}` : ''}`
      )
    );
    return response.data;
  }

  async applyFilters(
    payload: ApplyFiltersRequest
  ): Promise<ApplyFiltersResponse> {
    const response = await this.client.post<ApplyFiltersResponse>(
      manifestApiPath('/api/audience/apply-filters'),
      {
        ...payload,
        session_id: payload.session_id ?? null,
        audience_id: payload.audience_id ?? null,
        file_id: payload.file_id ?? null,
        tenant_id: payload.tenant_id ?? 'nexus-default'
      }
    );
    return response.data;
  }

  async listAudiences(params?: {
    page?: number;
    page_size?: number;
    status?: 'draft' | 'published';
    tenant_id?: string;
  }): Promise<AudienceListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page != null) searchParams.set('page', String(params.page));
    if (params?.page_size != null) {
      searchParams.set('page_size', String(params.page_size));
    }
    if (params?.status) searchParams.set('status', params.status);
    if (params?.tenant_id) searchParams.set('tenant_id', params.tenant_id);
    const query = searchParams.toString();
    const response = await this.client.get<AudienceListResponse>(
      manifestApiPath(`/api/audience${query ? `?${query}` : ''}`)
    );
    return response.data;
  }

  async deleteAudience(audienceId: string): Promise<DeleteAudienceResponse> {
    const response = await this.client.delete<DeleteAudienceResponse>(
      manifestApiPath(`/api/audience/${audienceId}`)
    );
    return response.data;
  }

  async uploadAudienceFile(
    file: File,
    options?: { session_id?: string | null }
  ): Promise<CsvUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.session_id) {
      formData.append('session_id', options.session_id);
    }

    const response = await this.client.post<CsvUploadResponse>(
      manifestApiPath('/api/audience/upload'),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  }

  async saveAudience(payload: SaveAudienceRequest): Promise<SaveAudienceResponse> {
    const response = await this.client.post<SaveAudienceResponse>(
      manifestApiPath('/api/audience/save'),
      {
        ...payload,
        status: payload.status ?? 'draft',
        audience_type: payload.audience_type ?? 'dynamic'
      }
    );
    return response.data;
  }

  async getAudience(audienceId: string): Promise<AudienceDetailResponse> {
    const response = await this.client.get<AudienceDetailResponse>(
      manifestApiPath(`/api/audience/${audienceId}`)
    );
    return response.data;
  }

  async updateAudience(
    audienceId: string,
    payload: SaveAudienceRequest
  ): Promise<SaveAudienceResponse> {
    const response = await this.client.patch<SaveAudienceResponse>(
      manifestApiPath(`/api/audience/${audienceId}`),
      payload
    );
    return response.data;
  }

  async exportAudienceById(audienceId: string): Promise<Blob> {
    const response = await fetch(
      this.manifestUrl(`/api/audience/${audienceId}/export`),
      getManifestFetchInit('GET', 'text/csv')
    );
    if (!response.ok) {
      throw new Error('Export failed');
    }
    return response.blob();
  }

  async exportAudienceBySql(payload: ExportAudienceRequest): Promise<Blob> {
    const response = await fetch(
      this.manifestUrl('/api/audience/export'),
      getManifestFetchInit(
        'POST',
        'text/csv',
        JSON.stringify({
          ...payload,
          tenant_id: payload.tenant_id ?? 'nexus-default'
        })
      )
    );
    if (!response.ok) {
      throw new Error('Export failed');
    }
    return response.blob();
  }
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export { downloadBlob };
